import { useEffect, useRef, useState } from "react";
import { fetchWithSupabaseAuth } from "../lib/supabaseClient.js";
import { invalidatePageDataCacheEntries } from "./usePageData.js";
import { dispatchPlanningStateChange } from "../utils/planningCrossViewSync.js";
import { tryBeginPlanningMutationActivity } from "../utils/planningMutationActivity.js";
import usePlanningSettingRefresh from "./usePlanningSettingRefresh.js";

export default function useEveningOrderPool(results, { unavailableReason = "" } = {}) {
  const refresh = usePlanningSettingRefresh(results);
  const [confirmed, setConfirmed] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");
  const [readState, setReadState] = useState(null);
  const inFlight = useRef(false);
  const loadSequence = useRef(0);
  const dispatchDate = String(results?.dispatch_date || "");
  const dispatchPeriod = results?.ui_dispatch_period || results?.dispatch_period || "day";
  const revision = String(results?.runtime_revision || "");
  const scope = `${dispatchDate}:${dispatchPeriod}`;
  const readKey = `${scope}:${revision}`;
  const currentScope = useRef(scope);
  currentScope.current = scope;
  const usesConfirmed = confirmed?.scope === scope
    && (confirmed.revision === revision || confirmed.previousRevisions.includes(revision));
  const enabled = dispatchPeriod === "evening" && (usesConfirmed ? confirmed.enabled : results?.evening_include_unassigned_day_orders === true);
  const expectedRevision = usesConfirmed ? confirmed.revision : revision;
  const readReady = readState?.key === readKey && readState.status === "ready";
  const disabledReason = unavailableReason || (dispatchPeriod === "evening"
    ? readState?.key === readKey && readState.status === "error"
      ? readState.error : !readReady ? "Chargement de l’option du Soir en cours." : ""
    : "Le dispatch Soir de cette date n’est pas encore disponible.");
  const available = Boolean(dispatchPeriod === "evening" && dispatchDate && expectedRevision && !disabledReason);

  useEffect(() => {
    if (dispatchPeriod !== "evening" || !dispatchDate) return undefined;
    const sequence = ++loadSequence.current;
    let cancelled = false;
    setReadState({ key: readKey, status: "loading" });
    fetchWithSupabaseAuth(`/api/planning/dispatch/${encodeURIComponent(dispatchDate)}/evening-order-pool`)
      .then(async response => {
        if (!response.ok) throw new Error("Impossible de lire l’option du Soir.");
        const payload = await response.json();
        if (!payload.runtime_revision || typeof payload.evening_include_unassigned_day_orders !== "boolean") throw new Error("Réponse de lecture du Soir invalide.");
        if (!cancelled && sequence === loadSequence.current) {
          setConfirmed({ scope, previousRevisions: [revision], revision: payload.runtime_revision, enabled: payload.evening_include_unassigned_day_orders });
          setReadState({ key: readKey, status: "ready" });
        }
      }).catch(failure => {
        if (!cancelled && sequence === loadSequence.current) setReadState({ key: readKey, status: "error", error: failure.message });
      });
    return () => { cancelled = true; };
  }, [dispatchDate, dispatchPeriod, revision, scope, readKey]);

  useEffect(() => { setError(""); }, [scope]);
  useEffect(() => {
    // A different day, period or independently updated planning owns its state.
    if (confirmed && !usesConfirmed) setConfirmed(null);
  }, [confirmed, usesConfirmed]);

  async function save(nextEnabled) {
    if (inFlight.current || !available) return;
    const finishActivity = tryBeginPlanningMutationActivity();
    if (!finishActivity) {
      setError("Une modification du planning est déjà en cours.");
      return;
    }
    inFlight.current = true;
    loadSequence.current += 1;
    setIsSaving(true);
    setError("");
    try {
      const params = new URLSearchParams({ dispatch_period: dispatchPeriod });
      const response = await fetchWithSupabaseAuth(`/api/planning/dispatch/${encodeURIComponent(dispatchDate)}/evening-order-pool?${params}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ enabled: nextEnabled, expected_runtime_revision: expectedRevision }),
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload?.error || payload?.detail?.error || (typeof payload?.detail === "string" ? payload.detail : "Impossible d’enregistrer l’ajout de commandes du Jour."));
      if (typeof payload.evening_include_unassigned_day_orders !== "boolean" || !payload.runtime_revision) throw new Error("Réponse de sauvegarde invalide.");
      if (currentScope.current === scope) {
        refresh.waitForRefresh(payload.runtime_revision);
        setConfirmed({
          scope,
          // A results request can still return any revision preceding these saves.
          previousRevisions: [...new Set([
            ...(usesConfirmed ? confirmed.previousRevisions : []), revision, expectedRevision,
          ])],
          revision: payload.runtime_revision,
          enabled: payload.evening_include_unassigned_day_orders,
        });
      }
    } catch (failure) {
      if (currentScope.current === scope) {
        setError(failure instanceof Error ? failure.message : "Impossible d’enregistrer l’ajout de commandes du Jour.");
      }
    } finally {
      invalidatePageDataCacheEntries(["/api/results/latest"]);
      // This endpoint returns a setting, not the updated planning. Reload locally too.
      // Empty period deliberately invalidates both Jour and Soir for this date.
      dispatchPlanningStateChange("dispatch", {
        dispatchDate, dispatchPeriod: "", action: "evening_order_pool", revalidateCurrentView: true,
      });
      inFlight.current = false;
      setIsSaving(false);
      finishActivity();
    }
  }
  return { enabled, isSaving: isSaving || refresh.isRefreshing, error: error || refresh.error, save, disabledReason, dispatchDate, summary: results?.evening_pool_summary, visible: dispatchPeriod === "evening", available };
}
